import IcArrowForward from '@deliveryhero/pd-cookbook/components/Icons/IcArrowForward';
import { Link } from '@deliveryhero/pd-cookbook/components/Link';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { Grid, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { UserRoles, UserStates } from 'services/api';
import { LabelText } from 'src/components/Employee/constants';
import { EmployeeDetailForm } from 'src/components/Forms/EmployeeDetailForm/EmployeeDetailForm';
import { EmployeeDetailFormValues } from 'src/components/Forms/EmployeeDetailForm/types';
import { PaddedPaper } from 'src/components/PaddedPaper';
import { RadioGroup } from 'src/components/RadioGroup';
import { formatUserSubscriptionDetails } from 'src/utils/format-user-subscription-details';
import isFoodora from 'src/utils/is-foodora/is-foodora';

import { AccountLinkStatus } from '../AccountLinkStatus';

import { EditEmployeeProps } from './types';

export const EditEmployee = (props: EditEmployeeProps) => {
  const {
    config,
    employee,
    employeeRole,
    setEmployeeRole,
    detailFormRef,
    handleDelete,
    handleUnlink,
    handleResendInvitationEmail,
    handleSubscriptionRedirection,
    ...others
  } = props;

  const initialValues: EmployeeDetailFormValues = {
    first_name: employee.first_name,
    last_name: employee.last_name,
    email: employee.email,
    mobile_country_code: employee.mobile_country_code,
    mobile_number: `${employee.mobile_country_code} ${employee.mobile_number}`,
    role: employee.role,
    department_id: employee.department.id,
    cost_center_id: employee.cost_center?.id ?? 0,
    expense_codes: employee.expense_codes.map((obj) => obj.id),
    employee_code: employee.employee_code,
    is_eligible_for_all_expense_codes:
      employee.is_eligible_for_all_expense_codes
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={8}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <PaddedPaper>
              <Grid container spacing={2}>
                <Grid item>
                  <Typography type="title-md">
                    {LabelText.EDIT_EMPLOYEE.BASIC_INFORMATION_TITLE}
                  </Typography>
                </Grid>
                <Grid item>
                  <EmployeeDetailForm
                    initialValues={initialValues}
                    detailFormRef={detailFormRef}
                    isNewUser={false}
                    isForRightDrawer={false}
                    countryCode={config.code}
                    {...others}
                  />
                </Grid>
              </Grid>
            </PaddedPaper>
          </Grid>
          {employee.subscription && (
            <Grid item>
              <PaddedPaper>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography type="title-md">
                      {LabelText.EDIT_EMPLOYEE.SERVICES_TITLE}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleSubscriptionRedirection()}
                      >
                        <ListItemText
                          sx={{
                            typography: 'labelLg',
                            color: 'neutral.primary'
                          }}
                          primary={isFoodora() ? 'foodora PRO' : 'pandapro'}
                        />
                        <ListItemText
                          primary={formatUserSubscriptionDetails(
                            employee.subscription,
                            config.currencyCode
                          )}
                          primaryTypographyProps={{ align: 'right' }}
                          sx={{
                            typography: 'paragraphSm',
                            color: 'neutral.primary'
                          }}
                        />
                        <IcArrowForward fill="neutral-secondary" />
                      </ListItemButton>
                    </ListItem>
                  </Grid>
                </Grid>
              </PaddedPaper>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <PaddedPaper>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <Typography type="title-md">
                    {LabelText.EDIT_EMPLOYEE.USER_ROLE_TITLE}
                  </Typography>
                </Grid>
                <Grid item>
                  <RadioGroup<UserRoles>
                    name="role"
                    value={employeeRole}
                    onChange={(value) => setEmployeeRole(value)}
                    radioProps={[
                      {
                        label: LabelText.EMPLOYEE_ROLE.USER,
                        value: UserRoles.User
                      },
                      {
                        label: LabelText.EMPLOYEE_ROLE.MANAGER,
                        value: UserRoles.Manager
                      }
                    ]}
                  />
                </Grid>
              </Grid>
            </PaddedPaper>
          </Grid>
          <Grid item>
            <PaddedPaper>
              <AccountLinkStatus
                employee={employee}
                handleUnlink={handleUnlink}
                handleResendInvitationEmail={handleResendInvitationEmail}
              />
            </PaddedPaper>
          </Grid>
          {Boolean(employee.state !== UserStates.Deleted) && (
            <Grid item>
              <PaddedPaper>
                <Grid container spacing={2}>
                  <Grid item>
                    <Typography type="title-md">
                      {LabelText.EDIT_EMPLOYEE.DELETE_PROFILE_TITLE}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography type="paragraph-sm">
                      Once deleted, this profile cannot be recovered
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography type="label-md">
                      <Link href="" onClick={handleDelete}>
                        {LabelText.EDIT_EMPLOYEE.DELETE_BUTTON}
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
              </PaddedPaper>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
